import { EventEmitter } from "events";
import { useEffect, useMemo, useRef, useState } from "react";

import { DocumentContainer } from "components/organisms/editor/collaboration/session/DocumentContainer";
import EditorWatchdogManager from "components/organisms/editor/collaboration/sharejs/editorWatchdogManager";
import { ShareJsSocket } from "components/organisms/editor/collaboration/sharejs/socket";
import { wsManuscriptDocumentUrl } from "utils/urls";

function buildDocumentState(baseDocument, currentDoc) {
    if (!currentDoc) {
        return baseDocument;
    }

    const rawSnapshot = currentDoc.getRawSnapshot();
    const content = currentDoc.getSnapshot();
    const currentVersion = currentDoc.getVersion();
    const liveRanges = currentDoc.getRanges?.();
    const otSnapshot =
        typeof rawSnapshot === "string"
            ? { content: rawSnapshot }
            : rawSnapshot || { content: baseDocument?.current_content || "" };

    return {
        ...baseDocument,
        node_id: baseDocument?.node_id,
        current_content: typeof content === "string" ? content : baseDocument?.current_content || "",
        current_version: typeof currentVersion === "number" ? currentVersion : baseDocument?.current_version,
        ot_type: "history-ot",
        ot_snapshot: otSnapshot,
        ot_ranges: {
            changes: listOrEmpty(liveRanges?.changes ?? otSnapshot.trackedChanges),
            comments: listOrEmpty(liveRanges?.comments ?? otSnapshot.comments),
        },
    };
}

function listOrEmpty(value) {
    return Array.isArray(value) ? value : [];
}

export function useManuscriptOTClient({
    spaceUuid,
    manuscriptId,
    nodeId,
    enabled = true,
    initialDocument,
    trackChangesUserId = null,
    onDocumentState,
    onPresenceEvent,
    onCursorEvent,
    onSocketError,
}) {
    const socketRef = useRef(null);
    const currentDocRef = useRef(null);
    const initialDocumentRef = useRef(initialDocument);
    const onDocumentStateRef = useRef(onDocumentState);
    const onPresenceEventRef = useRef(onPresenceEvent);
    const onCursorEventRef = useRef(onCursorEvent);
    const onSocketErrorRef = useRef(onSocketError);
    const trackChangesUserIdRef = useRef(trackChangesUserId);
    const [isConnected, setIsConnected] = useState(false);
    const [currentDoc, setCurrentDoc] = useState(null);
    const [isReady, setIsReady] = useState(false);
    const ideEventEmitter = useMemo(() => new EventEmitter(), []);
    const globalEditorWatchdogManager = useMemo(
        () =>
            new EditorWatchdogManager({
                onTimeoutHandler: (meta) => {
                    console.error("[manuscript-ot] editor watchdog timeout", meta);
                },
            }),
        [],
    );

    const closeSocketSafely = (docContainer, socket) => {
        const finalize = () => {
            socket.close();
            if (socketRef.current === socket) {
                socketRef.current = null;
            }
            if (currentDocRef.current === docContainer) {
                currentDocRef.current = null;
            }
            setCurrentDoc((current) => (current === docContainer ? null : current));
            setIsConnected(false);
            setIsReady(false);
        };

        if (!docContainer?.hasBufferedOps()) {
            finalize();
            return;
        }

        docContainer.flush();

        let closed = false;
        const finishOnce = () => {
            if (closed) {
                return;
            }
            closed = true;
            finalize();
        };

        docContainer.leave(() => {
            finishOnce();
        });

        window.setTimeout(() => {
            finishOnce();
        }, 1000);
    };

    useEffect(() => {
        initialDocumentRef.current = initialDocument;
    }, [initialDocument]);

    useEffect(() => {
        onDocumentStateRef.current = onDocumentState;
    }, [onDocumentState]);

    useEffect(() => {
        onPresenceEventRef.current = onPresenceEvent;
    }, [onPresenceEvent]);

    useEffect(() => {
        onCursorEventRef.current = onCursorEvent;
    }, [onCursorEvent]);

    useEffect(() => {
        onSocketErrorRef.current = onSocketError;
    }, [onSocketError]);

    useEffect(() => {
        trackChangesUserIdRef.current = trackChangesUserId;
        currentDocRef.current?.setTrackChangesUserId?.(trackChangesUserId || null);
    }, [trackChangesUserId]);

    const connectionReady = useMemo(
        () => enabled && !!spaceUuid && !!manuscriptId && !!nodeId,
        [enabled, manuscriptId, nodeId, spaceUuid],
    );

    useEffect(() => {
        if (!connectionReady) {
            setCurrentDoc(null);
            currentDocRef.current = null;
            setIsReady(false);
            return undefined;
        }

        const socket = new ShareJsSocket(wsManuscriptDocumentUrl(spaceUuid, manuscriptId, nodeId));
        const docContainer = new DocumentContainer(nodeId, socket, {
            type: "history-ot",
            ideEventEmitter,
            globalEditorWatchdogManager,
        });
        docContainer.setTrackChangesUserId(trackChangesUserIdRef.current || null);

        const emitDocumentState = () => {
            if (!docContainer.doc) {
                return;
            }
            setIsReady(true);
            const state = buildDocumentState(initialDocumentRef.current, docContainer);
            if (state) {
                onDocumentStateRef.current?.(state);
            }
        };

        socketRef.current = socket;
        currentDocRef.current = docContainer;
        setCurrentDoc(docContainer);

        const handleConnect = () => {
            setIsConnected(true);
            ideEventEmitter.emit("project:joined");
        };
        const handleDisconnect = () => {
            setIsConnected(false);
            setIsReady(false);
        };
        const handleSocketError = (error) => {
            onSocketErrorRef.current?.(error);
        };
        const handlePresence = (payload) => {
            onPresenceEventRef.current?.(payload);
        };
        const handleCursorUpdated = (payload, userId) => {
            onCursorEventRef.current?.({ payload, user_id: userId });
        };
        const handleDocumentSnapshot = (document) => {
            if (document?.node_id === nodeId) {
                initialDocumentRef.current = document;
                onDocumentStateRef.current?.(document);
            }
        };
        const handleContainerError = (error, meta) => {
            onSocketErrorRef.current?.({
                ...meta,
                message: error?.message || String(error),
            });
        };

        socket.on("connect", handleConnect);
        socket.on("disconnect", handleDisconnect);
        socket.on("error", handleSocketError);
        socket.on("presence", handlePresence);
        socket.on("cursor_updated", handleCursorUpdated);
        socket.on("document_snapshot", handleDocumentSnapshot);

        docContainer.on("saved", emitDocumentState);
        docContainer.on("op:acknowledged", emitDocumentState);
        docContainer.on("remoteop", emitDocumentState);
        docContainer.on("ranges:dirty", emitDocumentState);
        docContainer.on("ranges:redraw", emitDocumentState);
        docContainer.on("ranges:update", emitDocumentState);
        docContainer.on("error", handleContainerError);

        socket.connect();

        docContainer.join((error) => {
            if (error) {
                handleContainerError(error);
                return;
            }
            emitDocumentState();
        });

        return () => {
            docContainer.removeListener("saved", emitDocumentState);
            docContainer.removeListener("op:acknowledged", emitDocumentState);
            docContainer.removeListener("remoteop", emitDocumentState);
            docContainer.removeListener("ranges:dirty", emitDocumentState);
            docContainer.removeListener("ranges:redraw", emitDocumentState);
            docContainer.removeListener("ranges:update", emitDocumentState);
            docContainer.removeListener("error", handleContainerError);
            socket.removeListener("connect", handleConnect);
            socket.removeListener("disconnect", handleDisconnect);
            socket.removeListener("error", handleSocketError);
            socket.removeListener("presence", handlePresence);
            socket.removeListener("cursor_updated", handleCursorUpdated);
            socket.removeListener("document_snapshot", handleDocumentSnapshot);
            closeSocketSafely(docContainer, socket);
        };
    }, [connectionReady, globalEditorWatchdogManager, ideEventEmitter, manuscriptId, nodeId, spaceUuid]);

    return {
        currentDoc,
        isConnected,
        isReady,
        sendCursorUpdate: (payload) =>
            socketRef.current?.sendMessage({
                action: "cursor_update",
                payload,
            }) || false,
        flushNow: () => currentDocRef.current?.flush(),
    };
}

export default useManuscriptOTClient;
